import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { DatePicker, Drawer, Select, Input, Tooltip, Modal } from 'antd';
import { RedoOutlined, PlusOutlined, DownloadOutlined, CloseOutlined } from '@ant-design/icons';
import moment from 'moment';
import styled from 'styled-components';
import { RCTable, ButtonWrapper, PopoverWrapper, PopoverButton } from 'components';
import { Box, Button, Flex, Text } from 'atoms';
import { ACL, FilterTitle } from 'components/common';
import {
	getTripListAction,
	exportDownloadSlice,
	getTripFilterList,
	getListAction,
} from 'store/pageSlice';
import {
	DATE_FORMAT,
	initialPaginateConfig,
	TripHistoryTableConfig,
	DATE_FORMAT_WITH_TIME,
} from 'utils';
import { useQueryParams } from 'hooks';
import { FilterAtnButton } from 'utils/commonStyled';
import { ShowRouteMap } from './StoresRoutes';

const { Search } = Input;

const CustomModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 4px;
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
		padding: 0;
		text-align: center;
		padding-inline: 50px;
	}
	.ant-modal-body {
		padding: 0;
	}
`;

const ModalContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 28px;
	padding: 0px 0px 24px;
`;

const UpperContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 24px;
	padding: 20px 0px 0px;
	width: 100%;
`;

const WarningIcon = styled.div`
	svg {
		width: 36px;
		height: 32px;
	}
`;

const TextContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	align-self: stretch;
	gap: 13px;
`;

const TitleContainer = styled.div`
	display: flex;
	width: 470px;
	gap: 24px;
`;

const WarningText = styled.p`
	font-family: Inter, sans-serif;
	font-weight: 600;
	font-size: 16px;
	line-height: 1.25em;
	text-align: center;
	color: #000000;
	margin: 0;
`;

const QuestionContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-self: stretch;
	gap: 8px;
`;

const QuestionText = styled.p`
	font-family: Inter, sans-serif;
	font-weight: 400;
	font-size: 14px;
	line-height: 1.21em;
	color: #858384;
	margin: 0;
`;

const ActionButtons = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 28px;
	width: 100%;
	padding: 0 30px;
`;

const NoButton = styled(Button)`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 10px;
	padding: 8px 16px;
	width: 135.5px;
	height: 40px;
	border-radius: 2px;
	border: 0.5px solid #173c58;
	color: #173c58;
	font-family: Inter, sans-serif;
	font-weight: 500;
	font-size: 16px;
	line-height: 1.21em;
	background: #ffffff;
`;

const YesButton = styled(Button)`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 10px;
	padding: 8px 16px;
	width: 135.5px;
	height: 40px;
	border-radius: 2px;
	background: #143e60;
	color: #ffffff;
	font-family: Inter, sans-serif;
	font-weight: 500;
	font-size: 16px;
	line-height: 1.21em;
	border: none;
`;

const LtSearchBar = styled(Search)`
	border: none;
`;

const allOption = {
	value: '',
	label: 'All',
	key: 'All',
};

const TripStatusOptions = [
	{ value: '1', label: 'Reported' },
	{ value: '2', label: 'Trip Start' },
	{ value: '3', label: 'Trip End' },
];
TripStatusOptions.unshift(allOption);

const tripTypeList = [
	{ value: 'regular', label: 'Regular' },
	{ value: 'adhoc', label: 'Adhoc' },
];
tripTypeList.unshift(allOption);

const validOptions = [
	{ value: 'true', label: 'Valid', key: true },
	{ value: 'false', label: 'Invalid', key: false },
];
validOptions.unshift(allOption);

function populateOptions(data = [], showAll = true) {
	/**
	 * assign id & value to option list
	 */
	data = data?.map((element) => ({
		value: element?.id,
		label: element?.value,
	}));

	/**
	 * Add All option of array first
	 */
	if (showAll) {
		data.unshift(allOption);
	}

	return data;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function TripHistoryListPage() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { RangePicker } = DatePicker;
	const [citiesList, setCitiesList] = useState('');
	const [clientsList, setClientsList] = useState('');
	const [projectsList, setProjectsList] = useState('');
	const [vendorsList, setVendorsList] = useState('');
	const [businessUnitsList, setBusinessUnitsList] = useState([]);
	const [open, setOpen] = useState(false);
	const [rowData, setRowData] = useState({});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectTripLocations, setTripLocations] = useState();
	const [paginateConfig, setpaginateConfig] = useQueryParams({
		search_type: 'trip',
		fetch_filters: false,
		business_unit_ids: [],
	});
	const [exportModalVisible, setExportModalVisible] = useState(false);
	const [exportWarningModal, setExportWarningModal] = useState(false);

	const memoizedDispatchAction = useCallback(
		(params) => {
			// Ensure business_unit_ids is always an array in the API call
			const apiParams = {
				...params,
				fetch_filters: false,
				business_unit_ids: getBusinessUnitIds(),
			};
			return getTripListAction(apiParams);
		},
		[paginateConfig?.business_unit_ids]
	);

	useEffect(() => {
		['city', 'client', 'project', 'vendor'].forEach((filter) => {
			dispatch(getTripFilterList(filter)).then((res) => {
				const { rows = [] } = res;

				switch (filter) {
					case 'city': {
						const cities = rows?.cities?.map((element) => ({
							value: element,
							label: element,
						}));
						cities.unshift(allOption);
						setCitiesList(cities);
						break;
					}
					case 'client': {
						const clients = populateOptions(rows.clients);
						setClientsList(clients);
						break;
					}
					case 'project': {
						const projects = populateOptions(rows.projects);
						setProjectsList(projects);
						break;
					}
					case 'vendor': {
						const vendors = populateOptions(rows.vendors);
						setVendorsList(vendors);
						break;
					}
					default:
						break;
				}
			});
		});

		dispatch(
			getListAction({
				search_type: 'business_unit',
				limit: 100,
				offset: 0,
			})
		).then((res) => {
			const buOptions = (res?.rows || []).map((bu) => ({
				value: String(bu.id),
				label: bu.name,
			}));
			// Don't add "All" option for multiple select filters
			setBusinessUnitsList(buOptions);
		});
	}, [dispatch, paginateConfig]);

	// Calculate business unit filter value when business units list or IDs change
	useEffect(() => {
		const buIds = getBusinessUnitIds();
		if (buIds.length === 0) {
			setBusinessUnitFilterValue('');
			return;
		}

		// Ensure businessUnitsList is an array before using find
		if (!Array.isArray(businessUnitsList) || businessUnitsList.length === 0) {
			setBusinessUnitFilterValue('Business Unit');
			return;
		}

		// Get the business unit names for display
		const buNames = buIds
			.map((id) => {
				const businessUnit = businessUnitsList.find((item) => item.value === id);
				if (!businessUnit) {
					console.log(
						'Business unit not found for ID:',
						id,
						'Available options:',
						businessUnitsList
					);
				}
				return businessUnit ? businessUnit.label : id;
			})
			.filter(Boolean);

		setBusinessUnitFilterValue(buNames.length > 0 ? 'Business Unit' : '');
	}, [businessUnitsList, paginateConfig?.business_unit_ids]);

	const StatusValue = paginateConfig?.is_active ? 'Status' : '';
	const UserVal = paginateConfig?.user_type ? 'User Type' : '';
	const cityFilterValue = paginateConfig?.city ? 'City' : '';

	const getBusinessUnitIds = () => {
		const buIds = paginateConfig?.business_unit_ids;
		let result = [];

		if (Array.isArray(buIds)) {
			result = buIds;
		} else if (buIds) {
			result = [buIds];
		}

		return result.map((businessUnitId) => String(businessUnitId).trim()).filter(Boolean);
	};

	const [businessUnitFilterValue, setBusinessUnitFilterValue] = useState('');
	const client = paginateConfig?.client_id ? 'Client' : '';
	const project = paginateConfig?.project_id ? 'Project' : '';
	const StatusValueAll = paginateConfig?.is_active === '' ? 'Status' : '';
	const UserAllVal = paginateConfig?.user_type === '' ? 'User Type' : '';
	const clientAll = paginateConfig?.client_id === '' ? 'Client' : '';
	const projectAll = paginateConfig?.project_id === '' ? 'Project' : '';
	const cityAllValue = paginateConfig?.city === '' ? 'City' : '';

	const vendorValue = paginateConfig?.vendor_id === '' ? 'Vendor' : '';
	const VendorAllValue = paginateConfig?.vendor_id ? 'Vendor' : '';
	const dateValue = paginateConfig?.to_date ? 'Date' : '';
	const tripStatus = paginateConfig?.trip_status ? 'Status' : '';
	const tripAllStatus = paginateConfig?.trip_status === '' ? 'Status' : '';
	const tripType = paginateConfig?.trip_type ? 'Type' : '';
	const tripAllType = paginateConfig?.trip_type === '' ? 'Type' : '';
	const All_is_valid = paginateConfig?.is_valid === '' ? 'Valid' : '';
	const All_is_valid_fasle = paginateConfig?.is_valid === false ? 'Valid' : '';
	const is_valid = paginateConfig?.is_valid ? 'Valid' : '';

	const paginationArry = [
		cityFilterValue,
		businessUnitFilterValue,
		StatusValue,
		UserVal,
		client,
		project,
		projectAll,
		StatusValueAll,
		UserAllVal,
		clientAll,
		cityAllValue,
		vendorValue,
		VendorAllValue,
		dateValue,
		tripStatus,
		tripAllStatus,
		tripType,
		tripAllType,
		All_is_valid,
		is_valid,
		All_is_valid_fasle,
	];
	const validArray = paginationArry.filter((n) => n);
	const createExport = (toDate, fromDate) => {
		const payload = {
			create_type: 'download_task',
			data: {
				type: 'trip_export',
				filter_data: {
					to_date: toDate,
					from_date: fromDate,
					city: paginateConfig?.city,
					business_unit_ids: getBusinessUnitIds(),
					project_id: paginateConfig?.project_id,
					vendor_id: paginateConfig?.vendor_id,
					client_id: paginateConfig?.client_id,
					trip_type: paginateConfig?.trip_type,
					trip_status: paginateConfig?.trip_status,
					is_valid: getFilterData(paginateConfig?.is_valid),
					query_str: paginateConfig?.query_str,
				},
			},
		};
		dispatch(exportDownloadSlice({ data: payload, openNewWindow: false })).then((res) => {
			if (res?.rows) {
				setExportModalVisible(true);
			}
		});
	};

	const handleExportClick = () => {
		const toDate = paginateConfig?.to_date;
		const fromDate = paginateConfig?.from_date;

		if (!toDate || !fromDate || toDate === undefined || fromDate === undefined) {
			setExportWarningModal(true);
		} else {
			const diff = moment(toDate).diff(moment(fromDate), 'days');
			if (diff > 365) {
				setExportWarningModal(true);
			} else {
				createExport(toDate, fromDate);
			}
		}
	};

	const handleExportWarningModalConfirm = () => {
		const toDate = moment(new Date()).format(DATE_FORMAT_WITH_TIME);
		const fromDate = moment(toDate).subtract(60, 'days').format(DATE_FORMAT_WITH_TIME);

		setExportWarningModal(false);
		createExport(toDate, fromDate);
	};

	const handleExportWarningModalCancel = () => {
		setExportWarningModal(false);
	};

	const regx = /true/;

	// eslint-disable-next-line consistent-return
	function getFilterData(valid) {
		if (valid === '') {
			return 'All';
		}
		if (valid === undefined) {
			return 'not_selected';
		}
		if (regx.test(valid)) {
			return true;
		}
		if (regx.test(valid) === false && valid !== 'All' && valid !== undefined) {
			return false;
		}
	}

	function getFormattedDate(val) {
		const fomatedDate = moment.utc(val).format(DATE_FORMAT);
		const validData = `${fomatedDate} 00:00`;
		return moment.utc(validData).subtract(330, 'minutes').format(DATE_FORMAT_WITH_TIME);
	}
	function getFormattedEndDate(val) {
		const fomatedDate = moment.utc(val).format(DATE_FORMAT);
		const validData = `${fomatedDate} 23:59`;
		return moment.utc(validData).subtract(330, 'minutes').format(DATE_FORMAT_WITH_TIME);
	}

	function handleValidTripTypeChange(val) {
		const finalVal = val === '' && val !== undefined ? 'All' : val;
		setpaginateConfig((prev) => ({
			...prev,
			is_valid: finalVal,
		}));
	}

	function showTripLocationHistory(trip_id) {
		dispatch(getTripListAction({ search_type: 'trip_location_history', trip_id }))
			.then((res) => {
				setIsModalOpen(true);
				setTripLocations(res);
			})
			.catch((err) => {
				console.log('Error:', err);
			});
	}

	return (
		<>
			<Flex justifyContent="space-between" mt={3}>
				{/* <Box flex="0 0 35%">
					<SearchBar
						placeholder="Search"
						name="query_str"
						queryValue={paginateConfig?.query_str}
						onSearch={(val) =>
							setpaginateConfig({
								...paginateConfig,
								query_str: val,
							})
						}
					/>
				</Box> */}
				<Box flex="0 0 20%" marginRight="10px">
					<LtSearchBar
						placeholder="Search"
						enterButton="Search"
						allowClear
						onSearch={(val) =>
							setpaginateConfig({
								...paginateConfig,
								query_str: val,
							})
						}
					/>
				</Box>
				<Box flex="0 0 35%" className="filter-hightlight">
					<Box flex="0 0 35%" display="flex">
						<FilterAtnButton
							onClick={() => {
								setOpen(true);
							}}
						>
							FILTERS
						</FilterAtnButton>
						<span className="highlight-list">
							{StatusValue && <span className="ant-tag">{StatusValue} </span>}
							{StatusValueAll && <span className="ant-tag">{StatusValueAll} </span>}
							{projectAll && <span className="ant-tag">{projectAll} </span>}
							{UserVal && <span className="ant-tag">{UserVal} </span>}
							{cityFilterValue && <span className="ant-tag">{cityFilterValue} </span>}
							{businessUnitFilterValue && (
								<span className="ant-tag">{businessUnitFilterValue} </span>
							)}
							{client && <span className="ant-tag">{client}</span>}
							{project && <span className="ant-tag">{project} </span>}
							{UserAllVal && <span className="ant-tag">{UserAllVal} </span>}
							{clientAll && <span className="ant-tag">{clientAll} </span>}
							{cityAllValue && <span className="ant-tag">{cityAllValue} </span>}
							{VendorAllValue && <span className="ant-tag">{VendorAllValue} </span>}
							{vendorValue && <span className="ant-tag">{vendorValue} </span>}
							{dateValue && <span className="ant-tag">{dateValue} </span>}
							{tripStatus && <span className="ant-tag">{tripStatus} </span>}
							{tripAllStatus && <span className="ant-tag">{tripAllStatus} </span>}
							{tripType && <span className="ant-tag">{tripType} </span>}
							{tripAllType && <span className="ant-tag">{tripAllType} </span>}
							{All_is_valid && <span className="ant-tag">{All_is_valid} </span>}
							{is_valid && <span className="ant-tag">{is_valid} </span>}
							{All_is_valid_fasle && (
								<span className="ant-tag">{All_is_valid_fasle} </span>
							)}

							{validArray.length > 0 && (
								<Tooltip title="Clear filter">
									<CloseOutlined
										className="filter-close-icon"
										onClick={() => {
											window.location = `${window.origin}/trip-history`;
										}}
									/>
								</Tooltip>
							)}
						</span>
					</Box>
				</Box>
				<Flex>
					<ACL action="create" module="trip_bulk_upload">
						<ButtonWrapper
							onClick={() => navigate('/trips/bulk-upload')}
							icons={<PlusOutlined />}
						>
							BULK UPLOAD
						</ButtonWrapper>
					</ACL>
					<ButtonWrapper onClick={handleExportClick} icons={<DownloadOutlined />}>
						EXPORT
					</ButtonWrapper>
					<ButtonWrapper
						onClick={() =>
							setpaginateConfig({
								...initialPaginateConfig,
								search_type: 'trip',
								fetch_filters: false,
							})
						}
						icons={<RedoOutlined />}
					>
						SYNC
					</ButtonWrapper>
					<ACL action="create" module="trip">
						<PopoverWrapper
							content={
								<div style={{ display: 'flex', flexDirection: 'column' }}>
									<PopoverButton
										onClick={() => navigate('/trips/add?city_type=inter_city')}
									>
										Inter City
									</PopoverButton>
									<PopoverButton
										onClick={() => navigate('/trips/add?city_type=intra_city')}
									>
										Intra City
									</PopoverButton>
								</div>
							}
						>
							<ButtonWrapper icons={<PlusOutlined />}>CREATE</ButtonWrapper>
						</PopoverWrapper>
					</ACL>
				</Flex>
			</Flex>
			<Box mt={3}>
				<RCTable
					rowKey="id"
					tableDatas={[rowData, setRowData]}
					togglePaginate={[paginateConfig, setpaginateConfig]}
					dispatchAction={memoizedDispatchAction}
					columns={TripHistoryTableConfig(showTripLocationHistory)}
					scroll={{ x: true }}
				/>
			</Box>
			<Drawer
				className="userList-drawer"
				title={<FilterTitle onClose={() => setOpen(false)} />}
				placement="right"
				closable={false}
				onClose={() => setOpen(false)}
				open={open}
			>
				<Box>
					<Box mb="2rem">
						<Text mb="5px">Trip Date</Text>
						{/* <RangePicker
							name="date_filter"
							style={{ width: '100%' }}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									from_date: val ? getFormattedDate(val[0]) : false,
									to_date: val ? moment(val[1]).format(newFormate) : false,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
						/> */}
						<RangePicker
							name="date_filter"
							style={{ width: '100%' }}
							allowClear
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									from_date: val ? getFormattedDate(val[0]) : false,
									to_date: val ? getFormattedEndDate(val[1]) : false,
								}))
							}
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">City</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="city"
							value={paginateConfig?.city}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									city: val,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={citiesList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Business Unit</Text>
						<Select
							mode="multiple"
							showSearch
							style={{ width: '100%' }}
							name="business_unit_ids"
							value={getBusinessUnitIds()}
							onChange={(val) => {
								setpaginateConfig((prev) => ({
									...prev,
									business_unit_ids: val,
								}));
							}}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={businessUnitsList}
							maxTagCount="responsive"
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Client</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="client_name"
							value={paginateConfig?.client_name}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									client_id: val,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={clientsList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Project</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="project_location_code"
							value={paginateConfig?.project_location_code}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									project_id: val,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={projectsList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Vendor</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="vendor_name"
							value={paginateConfig?.vendor_name}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									vendor_id: val,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={vendorsList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Trip Type</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="trip_type"
							value={paginateConfig?.trip_type}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									trip_type: val,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={tripTypeList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Trip Status</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="trip_status"
							value={paginateConfig?.trip_status}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									trip_status: val,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={TripStatusOptions}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Valid</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="is_valid"
							value={paginateConfig?.is_valid}
							onChange={(val) => handleValidTripTypeChange(val)}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={validOptions}
						/>
					</Box>

					<Box>
						<ButtonWrapper
							marginLeft="auto"
							border="1px solid rgba(237, 108, 2, 0.5)"
							color="#ed6c02"
							onClick={() => {
								window.location = `${window.origin}/trip-history`;
							}}
						>
							RESET FILTERS
						</ButtonWrapper>
					</Box>
				</Box>
			</Drawer>
			<Modal
				title="Your Report Request is in progress"
				visible={exportModalVisible}
				onCancel={() => setExportModalVisible(false)}
				footer={null}
				style={{ top: 220, marginLeft: 'calc(50% - 200px)' }}
			>
				<p style={{ color: '#666666' }}>Report will be available in the Downloads page.</p>

				<Button
					type="primary"
					style={{ marginTop: '25px' }}
					onClick={() => {
						setExportModalVisible(false);
						navigate('/downloads');
					}}
				>
					Go to Downloads page
				</Button>
			</Modal>
			<CustomModal
				open={exportWarningModal}
				onCancel={() => setExportWarningModal(false)}
				footer={null}
				closable={false}
				style={{ top: 200 }}
				width={488}
			>
				<ModalContainer>
					<UpperContent>
						<WarningIcon>
							<svg
								width="36"
								height="32"
								viewBox="0 0 36 32"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M1.76885 31.75C1.44802 31.75 1.15635 31.6701 0.893854 31.5103C0.631354 31.3504 0.427188 31.1387 0.281354 30.875C0.135521 30.6113 0.0556043 30.3267 0.0416043 30.021C0.0276043 29.7153 0.107521 29.4167 0.281354 29.125L16.4689 1.125C16.6439 0.833333 16.8702 0.614583 17.1479 0.46875C17.4255 0.322917 17.7096 0.25 18.0001 0.25C18.2906 0.25 18.5753 0.322917 18.8541 0.46875C19.1329 0.614583 19.3587 0.833333 19.5314 1.125L35.7189 29.125C35.8939 29.4167 35.9744 29.7159 35.9604 30.0228C35.9464 30.3296 35.8659 30.6137 35.7189 30.875C35.5719 31.1363 35.3677 31.3481 35.1064 31.5103C34.845 31.6724 34.5534 31.7523 34.2314 31.75H1.76885ZM4.7876 28.25H31.2126L18.0001 5.5L4.7876 28.25ZM18.0001 26.5C18.4959 26.5 18.9119 26.332 19.2479 25.996C19.5839 25.66 19.7513 25.2447 19.7501 24.75C19.7489 24.2553 19.5809 23.84 19.2461 23.504C18.9113 23.168 18.4959 23 18.0001 23C17.5043 23 17.0889 23.168 16.7541 23.504C16.4193 23.84 16.2513 24.2553 16.2501 24.75C16.2489 25.2447 16.4169 25.6606 16.7541 25.9977C17.0913 26.3349 17.5066 26.5023 18.0001 26.5ZM18.0001 21.25C18.4959 21.25 18.9119 21.082 19.2479 20.746C19.5839 20.41 19.7513 19.9947 19.7501 19.5V14.25C19.7501 13.7542 19.5821 13.3388 19.2461 13.004C18.9101 12.6692 18.4948 12.5012 18.0001 12.5C17.5054 12.4988 17.0901 12.6668 16.7541 13.004C16.4181 13.3412 16.2501 13.7565 16.2501 14.25V19.5C16.2501 19.9958 16.4181 20.4118 16.7541 20.7478C17.0901 21.0838 17.5054 21.2512 18.0001 21.25Z"
									fill="#FF3B30"
								/>
							</svg>
						</WarningIcon>
						<TextContainer>
							<TitleContainer>
								<WarningText>
									You are trying to export a large volume of data. Limit your
									selection to a shorter date range
								</WarningText>
							</TitleContainer>
							<QuestionContainer>
								<QuestionText>
									Do you want to proceed with last 60 days data
								</QuestionText>
							</QuestionContainer>
						</TextContainer>
					</UpperContent>
					<ActionButtons>
						<NoButton onClick={handleExportWarningModalCancel}>No</NoButton>
						<YesButton type="primary" onClick={handleExportWarningModalConfirm}>
							Yes
						</YesButton>
					</ActionButtons>
				</ModalContainer>
			</CustomModal>
			<ShowRouteMap
				isModalOpen={isModalOpen}
				tripdata={selectTripLocations}
				modalState={setIsModalOpen}
			/>
		</>
	);
}
